import React, { useState, useRef, useEffect } from 'react';
import { useI18n } from '../context/I18nContext';
import fileService from '../../services/file.service';

/**
 * FileUploadField Component
 * Handles S3 file uploads with presigned URLs
 * 
 * Features:
 * - Drag and drop support
 * - Progress tracking
 * - File validation
 * - Download/delete uploaded files
 * - Multiple file support per field
 * 
 * Props:
 * - fieldId: Form field ID
 * - applicationId: Current form application ID
 * - formId: Form type ID
 * - value: Array of uploaded attachments
 * - onChange: Callback when attachments change
 * - accept: File accept pattern (e.g., ".pdf,.docx")
 * - maxFiles: Maximum files per field (default: 5)
 * - maxSize: Maximum file size in MB (default: 50)
 * - readOnly: Disable uploads if true
 * - disabled: Disable input
 */
const FileUploadField = ({
    fieldId,
    applicationId,
    formId,
    value = [],
    onChange,
    accept = '.pdf,.docx,.jpg,.jpeg,.png',
    maxFiles = 5,
    maxSize = 50,
    readOnly = false,
    disabled = false,
    onRequireSave = null, // Callback to save form and get applicationId
}) => {
    const { t, getText } = useI18n(['forms', 'common']);
    
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const [error, setError] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [currentAppId, setCurrentAppId] = useState(applicationId);
    const fileInputRef = useRef(null);

    // Keep track of applicationId changes
    const effectiveAppId = currentAppId || applicationId;

    // Sync external applicationId changes
    useEffect(() => {
        if (applicationId && !currentAppId) {
            setCurrentAppId(applicationId);
        }
    }, [applicationId]);

    /**
     * Get presigned upload URL from backend
     */
    const getPresignedUrl = async (file, appId) => {
        try {
            const result = await fileService.getUploadUrl({
                formId,
                applicationId: appId || effectiveAppId,
                fieldId,
                filename: file.name,
                contentType: file.type,
                contentLength: file.size
            });
            return result;
        } catch (err) {
            throw err;
        }
    };

    /**
     * Upload file to S3 using presigned URL
     */
    const uploadFileToS3 = async (file, uploadUrl, s3Key) => {
        try {
            await fileService.uploadFileToS3(
                file,
                uploadUrl,
                (percentComplete) => {
                    setUploadProgress(prev => ({
                        ...prev,
                        [file.name]: percentComplete
                    }));
                }
            );
            return { s3Key };
        } catch (err) {
            throw err;
        }
    };

    /**
     * Complete upload by recording attachment metadata
     */
    const completeUpload = async (file, s3Key, appId) => {
        try {
            const result = await fileService.completeUpload({
                applicationId: appId || effectiveAppId,
                fieldId,
                s3Key,
                filename: file.name,
                contentType: file.type,
                contentLength: file.size
            });
            return result;
        } catch (err) {
            throw err;
        }
    };

    /**
     * Validate file before upload
     */
    const validateFile = (file) => {
        const validation = fileService.validateFile(file, {
            maxSize,
            accept
        });
        return validation.error;
    };

    /**
     * Handle file selection/drop
     */
    const handleFiles = async (files) => {
        setError(null);
        const fileArray = Array.from(files);
        let appIdForUpload = effectiveAppId;

        // If no applicationId but we have a save callback, try to save first
        if (!effectiveAppId && onRequireSave) {
            try {
                setError(null);
                const newAppId = await onRequireSave();
                if (!newAppId) {
                    setError('Unable to save form. Please save manually first.');
                    return;
                }
                setCurrentAppId(newAppId);
                appIdForUpload = newAppId;
            } catch (err) {
                setError(`Failed to save form: ${err.message}`);
                return;
            }
        }

        // Check if we still don't have applicationId
        if (!appIdForUpload) {
            setError('Please save the form first before uploading files');
            return;
        }

        // Check file count
        const totalFiles = (value?.length || 0) + fileArray.length;
        if (totalFiles > maxFiles) {
            setError(`Maximum ${maxFiles} files allowed per field`);
            return;
        }

        // Validate all files first
        for (const file of fileArray) {
            const validationError = validateFile(file);
            if (validationError) {
                setError(validationError);
                return;
            }
        }

        setUploading(true);
        const uploadedAttachments = [];

        try {
            for (const file of fileArray) {
                try {
                    // Get presigned URL
                    const urlData = await getPresignedUrl(file, appIdForUpload);
                    const { uploadUrl, s3Key } = urlData;

                    // Upload to S3
                    await uploadFileToS3(file, uploadUrl, s3Key);

                    // Complete upload (record metadata)
                    const result = await completeUpload(file, s3Key, appIdForUpload);
                    uploadedAttachments.push(result.attachment);

                    // Clear progress for this file
                    setUploadProgress(prev => {
                        const newProgress = { ...prev };
                        delete newProgress[file.name];
                        return newProgress;
                    });
                } catch (fileError) {
                    setError(`Failed to upload ${file.name}: ${fileError.message}`);
                    setUploading(false);
                    return;
                }
            }

            // Update parent with new attachments
            const newValue = [...(value || []), ...uploadedAttachments];
            onChange && onChange(newValue);
            
            // Clear file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } finally {
            setUploading(false);
            setUploadProgress({});
        }
    };

    /**
     * Delete uploaded file
     */
    const handleDelete = async (index) => {
        try {
            await fileService.deleteAttachment(effectiveAppId, index);

            // Remove from local state
            const newValue = value.filter((_, i) => i !== index);
            onChange && onChange(newValue);
        } catch (err) {
            setError(`Failed to delete file: ${err.message}`);
        }
    };

    /**
     * Download uploaded file
     */
    const handleDownload = async (index) => {
        try {
            const result = await fileService.getDownloadUrl(effectiveAppId, index);
            const { downloadUrl } = result;

            // Open download URL in new tab
            window.open(downloadUrl, '_blank');
        } catch (err) {
            setError(`Failed to download file: ${err.message}`);
        }
    };

    // Handle click on file input
    const handleClick = () => {
        if (!readOnly && !disabled && fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // Handle drag events
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (!readOnly && !disabled && e.dataTransfer.files) {
            handleFiles(e.dataTransfer.files);
        }
    };

    // Handle file input change
    const handleInputChange = (e) => {
        if (e.target.files) {
            handleFiles(e.target.files);
        }
    };

    const isLoading = uploading || Object.keys(uploadProgress).length > 0;

    return (
        <div className="space-y-3">
            {/* Upload area */}
            {!readOnly && (
                <div
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={handleClick}
                    className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                        dragActive
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                    } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept={accept}
                        onChange={handleInputChange}
                        disabled={isLoading || disabled}
                        className="hidden"
                    />

                    <div className="space-y-2">
                        <div className="text-gray-600">
                            <svg
                                className="mx-auto h-12 w-12 text-gray-400"
                                stroke="currentColor"
                                fill="none"
                                viewBox="0 0 48 48"
                            >
                                <path
                                    d="M28 8H12a4 4 0 00-4 4v20a4 4 0 004 4h24a4 4 0 004-4V20m-8-12l-4-4m0 0L20 8m4 0v12"
                                    strokeWidth={2}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-900">
                                {isLoading ? 'Uploading...' : 'Drag and drop files here'}
                            </p>
                            <p className="text-xs text-gray-500">
                                or click to select ({maxFiles - (value?.length || 0)} remaining)
                            </p>
                        </div>
                        <p className="text-xs text-gray-400">
                            Max {maxSize}MB per file • Accepted: {accept || 'all'}
                        </p>
                    </div>
                </div>
            )}

            {/* Error message */}
            {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-700">{error}</p>
                </div>
            )}

            {/* Upload progress */}
            {Object.entries(uploadProgress).map(([filename, progress]) => (
                <div key={filename} className="space-y-1">
                    <div className="flex justify-between items-center">
                        <p className="text-sm text-gray-700 truncate">{filename}</p>
                        <p className="text-xs text-gray-500">{Math.round(progress)}%</p>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-200"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>
            ))}

            {/* Uploaded files list */}
            {Array.isArray(value) && value.length > 0 && (
                <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-700">
                        Uploaded Files ({value.length})
                    </h4>
                    <div className="space-y-2">
                        {value.map((attachment, index) => (
                            <div
                                key={index}
                                className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors"
                            >
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                        {attachment.originalFilename}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {(attachment.size / 1024).toFixed(1)}KB • Uploaded by{' '}
                                        {attachment.uploadedBy?.displayName || 'Unknown'}
                                    </p>
                                </div>

                                <div className="flex items-center gap-2 ml-4">
                                    {!readOnly && (
                                        <button
                                            type="button"
                                            onClick={() => handleDelete(index)}
                                            className="text-red-600 hover:text-red-700 text-sm font-medium"
                                            title="Delete file"
                                        >
                                            ✕
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => handleDownload(index)}
                                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                                        title="Download file"
                                    >
                                        ↓
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FileUploadField;
